<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { serviceTypeDisplay, type Service } from '@/types';
import { descendantServiceIds } from '../composables/useBbelScope';

const props = defineProps<{
  show: boolean;
  /** Name of the service whose parents are being edited (for the modal copy). */
  serviceName: string;
  /** Id of that service — used to exclude self and its descendants from the options. */
  serviceId: string;
  /** All services in the workflow (the option source). */
  services: Service[];
  /** The service's current parentServiceIds — preselected when the modal opens. */
  currentParentIds: string[];
}>();

const emit = defineEmits<{
  confirm: [parentIds: string[]];
  cancel: [];
}>();

const selectedIds = ref<string[]>([]);
const search = ref('');

// Candidate parents: every other service except this one and anything downstream of it (selecting a
// descendant would close a loop). The server's no-cycle check remains the backstop for edge cases.
const candidates = computed(() => {
  const blocked = descendantServiceIds(props.services, props.serviceId);
  blocked.add(props.serviceId);
  return props.services
    .filter((s) => !blocked.has(s.id))
    .map((s) => ({
      id: s.id,
      name: s.name,
      typeIcon: serviceTypeDisplay(s.serviceModelType?.$type).icon,
    }));
});

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return candidates.value;
  return candidates.value.filter((s) => s.name.toLowerCase().includes(term));
});

// Reset the working selection from the service's current parents each time the modal opens, so a
// cancelled edit never leaks into the next open.
watch(
  () => props.show,
  (show) => {
    if (show) {
      selectedIds.value = [...props.currentParentIds];
      search.value = '';
    }
  }
);
</script>

<template>
  <div
    class="modal fade"
    :class="{ show, 'd-block': show }"
    @click.self="emit('cancel')"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title d-flex align-items-center gap-2">
            <i class="bi bi-diagram-3 text-primary"></i>
            Edit Parent Services
          </h2>
          <button type="button" class="btn-close" @click="emit('cancel')"></button>
        </div>

        <div class="modal-body">
          <p class="text-muted small mb-2">
            Choose which services <strong>{{ serviceName }}</strong> runs after.
          </p>

          <div class="input-group input-group-sm mb-2">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              v-model="search"
              type="text"
              class="form-control"
              placeholder="Filter services…"
            />
          </div>

          <div
            v-if="filtered.length"
            class="list-group border rounded overflow-auto"
            style="max-height: 320px"
          >
            <label
              v-for="s in filtered"
              :key="s.id"
              class="list-group-item d-flex align-items-center gap-2 mb-0"
            >
              <input
                class="form-check-input m-0"
                type="checkbox"
                :value="s.id"
                v-model="selectedIds"
              />
              <i :class="s.typeIcon" class="text-secondary"></i>
              <span class="text-truncate">{{ s.name }}</span>
            </label>
          </div>
          <p v-else class="text-muted small mb-0">No eligible parent services.</p>

          <div class="small text-muted mt-2">{{ selectedIds.length }} selected</div>
        </div>

        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" @click="emit('cancel')">
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="emit('confirm', selectedIds)"
          >
            <i class="bi bi-check-lg me-2"></i>Save Relationships
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
